import type { ContractWithRenewal } from '@services/contracts/mutations/useAnswerRenewal'
import { useContracts } from '@services/contracts/queires/useContracts'
import { format, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

/**
 * The main tenant's "extend or not" answer for the current end date, as
 * given in the app. Renders nothing until they have answered.
 */
export function TenantRenewalAnswer({ offerId }: { offerId: string }) {
  const { t } = useTranslation('offers')
  const { data: contracts } = useContracts({
    params: { offer: offerId, type: 'offer' }
  })
  const contract = contracts?.results?.find((c) => c.status === 'completed') as
    | ContractWithRenewal
    | undefined
  const renewal = contract?.renewal

  if (!renewal?.answer) return null

  const extend = renewal.answer === 'EXTEND'
  return (
    <div className="w-full space-y-2">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {t('admin.actions.renewal_answer')}
      </div>
      <div className="flex items-start justify-between gap-2 rounded-xl border border-ink/[0.06] bg-white px-3 py-2 text-sm">
        <div className="min-w-0">
          {renewal.note && <div className="text-ink">{renewal.note}</div>}
          {renewal.answered_at && (
            <div className="text-xs text-muted-foreground">
              {format(parseISO(renewal.answered_at), 'dd.MM.yyyy HH:mm')}
            </div>
          )}
        </div>
        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold',
            extend ? 'bg-primary/10 text-primary' : 'bg-peach/30 text-warning'
          )}
        >
          {extend
            ? t('admin.actions.renewal_extend')
            : t('admin.actions.renewal_not_extend')}
        </span>
      </div>
    </div>
  )
}
